import type { SupplierOrderItemDto } from '../../../dto/supplierOrder';
import type { OrderStockLine } from '../api/orderStockStatusApi';

/**
 * Read model of the ZD card „Skąd wiadomo, co zamówić”: each ZD line next to the
 * stock of its variant and what the source customer order (ZK) still misses
 * (`GET /orders/:id/stock-status`, quantities only — never a price). Pure: the
 * component formats the quantities with `units.ts`.
 *
 * `onHand` / `missing` are `null` when the stock read is unavailable or the line
 * is not in the ZK — the card shows „—”, never a made-up 0.
 */
export interface OriginQuantity {
  value: number;
  /** Unit of the ZK line the figure was computed in. */
  unit: string;
}

export interface SupplierOrderOriginRow {
  itemId: string;
  item: SupplierOrderItemDto;
  onHand: OriginQuantity | null;
  missing: OriginQuantity | null;
}

export interface OriginMissingLine {
  name: string;
  quantity: OriginQuantity;
}

const quantity = (value: number | null, unit: string): OriginQuantity | null => (
  value == null || !Number.isFinite(value) ? null : { value, unit }
);

/**
 * Shortage of several ZK lines of one variant: summed only when all are known
 * and in the same unit, otherwise only the first line's figure.
 */
function sumMissing(lines: OrderStockLine[]): OriginQuantity | null {
  if (lines.length === 0) return null;
  let total = quantity(lines[0].missing, lines[0].unit);
  for (const line of lines.slice(1)) {
    if (total && total.unit === line.unit) {
      total = line.missing == null ? null : { value: total.value + line.missing, unit: line.unit };
    }
  }
  return total;
}

/**
 * Pairs each ZD line with the ZK line it came from. „Utwórz szkice ZD” on the
 * ZK card (supplierDraftGroupingService, migration 207) records the exact link
 * in `SupplierDraftSourceLine`, read as `item.sourceOrderProductId`: such a line
 * shows the shortage of that ZK line only, also after a hand edit of the draft
 * or a second grouping run. A link to a ZK line no longer in stock-status shows
 * „—”. Lines without a link (pre-207 drafts, hand-added lines) fall back to
 * index pairing within the variant over the ZK lines not linked in this ZD: the
 * n-th unlinked ZD line pairs with the n-th such ZK line, the last one also
 * takes the shortage of any left over (one ZD line for two ZK lines shows their
 * sum), and unlinked lines beyond them show „—”. The shortage is never repeated
 * on two ZD rows.
 */
export function buildOriginRows(
  items: SupplierOrderItemDto[],
  stockLines: OrderStockLine[] | null | undefined,
): SupplierOrderOriginRow[] {
  const linesByVariant = new Map<string, OrderStockLine[]>();
  for (const line of stockLines || []) {
    if (!line.productVariantId) continue;
    linesByVariant.set(line.productVariantId, [...(linesByVariant.get(line.productVariantId) || []), line]);
  }
  const linkedLineIds = new Set(items.map((item) => item.sourceOrderProductId).filter(Boolean));
  const unlinkedLinesByVariant = new Map<string, OrderStockLine[]>();
  linesByVariant.forEach((lines, variantId) => {
    unlinkedLinesByVariant.set(variantId, lines.filter((line) => !linkedLineIds.has(line.orderProductId)));
  });
  const unlinkedCountByVariant = new Map<string, number>();
  for (const item of items) {
    if (item.productVariantId && !item.sourceOrderProductId) {
      unlinkedCountByVariant.set(item.productVariantId, (unlinkedCountByVariant.get(item.productVariantId) || 0) + 1);
    }
  }
  const seenByVariant = new Map<string, number>();
  return items.map((item) => {
    const variantLines = item.productVariantId ? linesByVariant.get(item.productVariantId) || [] : [];
    if (!item.productVariantId || variantLines.length === 0) {
      return { itemId: item.id, item, onHand: null, missing: null };
    }
    // The stock is the same physical pile for every line of the variant.
    const onHand = quantity(variantLines[0].onHand, variantLines[0].unit);
    if (item.sourceOrderProductId) {
      const source = variantLines.find((line) => line.orderProductId === item.sourceOrderProductId);
      return { itemId: item.id, item, onHand, missing: source ? quantity(source.missing, source.unit) : null };
    }
    const unlinkedLines = unlinkedLinesByVariant.get(item.productVariantId) || [];
    const index = seenByVariant.get(item.productVariantId) || 0;
    seenByVariant.set(item.productVariantId, index + 1);
    const isLast = index === (unlinkedCountByVariant.get(item.productVariantId) || 1) - 1;
    const paired = isLast ? unlinkedLines.slice(index) : unlinkedLines.slice(index, index + 1);
    return { itemId: item.id, item, onHand, missing: sumMissing(paired) };
  });
}

/** ZK lines the warehouse does not cover now (stock-status `MISSING`), for the „Powiadomienie” note. */
export function listMissingLines(stockLines: OrderStockLine[] | null | undefined): OriginMissingLine[] {
  return (stockLines || [])
    .filter((line) => line.state === 'MISSING' && line.missing != null && line.missing > 0)
    .map((line) => ({ name: line.name, quantity: { value: line.missing as number, unit: line.unit } }));
}
